import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api, { money, errMsg } from "../lib/api";
import { toast } from "sonner";
import { motion } from "framer-motion";
import { Upload, Boxes, Coins, ArrowRight, Sparkles, Wallet, TrendingDown, ShoppingCart, FileText } from "lucide-react";
import DatePicker from "@/components/ui/DatePicker";
import { todayIST } from "../lib/datetime";
import useAutoRefresh from "../lib/useAutoRefresh";

// The manager's day, in the order it happens. Every entry is a screen the
// manager is actually ALLOWED to open — the roles come from the same nav
// table the sidebar renders, so this list cannot promise a button that 403s.
const DAILY_TASKS = [
  {
    to: "/cashbook", testId: "qa-cash-book", icon: Wallet,
    title: "Daily Cash Book",
    desc: "Record today's expenses, receipts & bill proofs",
    border: "border-amber-500", tint: "bg-amber-50 text-amber-900 border-amber-200/80",
  },
  {
    to: "/inventory?section=daily_out", testId: "qa-stock-out", icon: TrendingDown,
    title: "Daily Stock Out",
    desc: "Log what left the store for floor & services",
    border: "border-slate-900", tint: "bg-slate-100 text-slate-900 border-slate-200",
  },
  {
    to: "/incentives", testId: "qa-incentives", icon: Coins,
    title: "Incentives",
    desc: "Live staff tier & bonus progress",
    border: "border-amber-500", tint: "bg-amber-50 text-amber-900 border-amber-200/80",
  },
  {
    to: "/pos", testId: "qa-pos-upload", icon: Upload,
    title: "Import POS CSV",
    desc: "Daily reconciliation & revenue sync",
    border: "border-slate-900", tint: "bg-slate-100 text-slate-900 border-slate-200",
  },
  {
    to: "/inventory", testId: "qa-checkout-stock", icon: Boxes,
    title: "Checkout Stock",
    desc: "Store → floor usage as it happens",
    border: "border-slate-900", tint: "bg-slate-100 text-slate-900 border-slate-200",
  },
  {
    to: "/procurement", testId: "qa-purchase-orders", icon: ShoppingCart,
    title: "Raise Purchase Orders",
    desc: "Turn out-of-stock & low-stock rows into POs",
    border: "border-amber-500", tint: "bg-amber-50 text-amber-900 border-amber-200/80",
  },
  {
    // Deep-links the Inventory Hub straight to its Purchase Invoices tab, the
    // same ?section= mechanism the Daily Stock Out card already uses. /inventory
    // is owner+manager and that section is not role-gated, so the button never
    // 403s behind a manager.
    to: "/inventory?section=purchase_invoices", testId: "qa-purchase-invoices", icon: FileText,
    title: "Purchase Invoices",
    desc: "Log vendor bills & GST against stock",
    border: "border-amber-500", tint: "bg-amber-50 text-amber-900 border-amber-200/80",
  },
];

export default function ManagerDashboard() {
  const [dates, setDates] = useState([]);
  const [day, setDay] = useState(null);
  const [rows, setRows] = useState([]);
  const [poDrafts, setPoDrafts] = useState([]);
  const [pageLoading, setPageLoading] = useState(true);

  useEffect(() => {
    Promise.all([
      api.get("/pos/dates").then((r) => {
        setDates(r.data);
        if (r.data[0]) setDay(r.data[0]);
      }),
      api.get("/inventory/purchase-orders").then((r) => setPoDrafts(r.data))
    ]).finally(() => setPageLoading(false));
  }, []);

  useEffect(() => {
    if (!day) return;
    api
      .get(`/incentives/daily?day=${day}`)
      .then((r) => setRows(r.data.rows))
      .catch((e) => toast.error(errMsg(e)));
  }, [day]);

  // Keep the day's incentive rows and the PO draft pile current. The date list
  // is deliberately NOT polled — it would reset the day the user picked.
  useAutoRefresh(() => {
    api.get("/inventory/purchase-orders").then((r) => setPoDrafts(r.data)).catch(() => {});
    if (day) {
      api.get(`/incentives/daily?day=${day}`).then((r) => setRows(r.data.rows)).catch(() => {});
    }
  }, 30000);

  const poNeeds = poDrafts.filter((p) => p.needs_reorder);

  if (pageLoading) return (
    <div className="p-6 sm:p-10 max-w-7xl mx-auto space-y-8 animate-pulse">
      <div className="h-40 bg-slate-200 rounded-2xl w-full"></div>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        {[1, 2, 3].map(i => <div key={i} className="h-32 bg-slate-200 rounded-xl"></div>)}
      </div>
      <div className="h-96 bg-slate-200 rounded-xl w-full"></div>
    </div>
  );

  return (
    <div className="p-6 sm:p-10 max-w-7xl mx-auto space-y-8 animate-in fade-in duration-300">
      {/* Header Banner */}
      <motion.div
        initial={{ opacity: 0, y: -10 }}
        animate={{ opacity: 1, y: 0 }}
        className="flex items-center justify-between gap-6 py-4"
      >
        <div>
          <div className="flex items-center gap-2 mb-2">
            <Sparkles className="w-4 h-4 text-amber-800" />
            <span className="text-slate-600 font-extrabold text-xs uppercase tracking-widest">
              SALON OPERATIONS DESK
            </span>
          </div>
          <h1 className="font-serif-lux text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight text-slate-900">
            Today at the Salon
          </h1>
          <p className="text-slate-600 font-medium text-sm sm:text-base mt-2 max-w-xl leading-relaxed">
            The day's checklist — cash book, stock out, incentives, orders.
          </p>
        </div>

        {/* Framed Aesthetic Salon Image Card */}
        <div className="hidden lg:block relative neu-card p-2 h-40 w-72 shrink-0 overflow-hidden">
          <img src="/assets/salon_plants.png" alt="Salon Aesthetic" className="w-full h-full object-cover rounded-2xl" />
        </div>
      </motion.div>

      {/* Daily Tasks — the manager's checklist, one card per job */}
      <div>
        <div className="flex flex-wrap items-baseline justify-between gap-2 mb-3">
          <div className="lss-overline">Daily Tasks</div>
          <span className="text-xs font-bold text-slate-500">
            Cash · stock · incentives · orders — the day's checklist
          </span>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {DAILY_TASKS.map((t) => (
            <motion.div key={t.to} whileHover={{ y: -3 }}>
              <Link
                to={t.to}
                data-testid={t.testId}
                className={`lss-card p-5 h-full flex items-start gap-3.5 hover:border-amber-400 border-t-4 ${t.border} group transition-all`}
              >
                <div className={`p-2.5 rounded-xl border shrink-0 shadow-xs group-hover:scale-105 transition-transform ${t.tint}`}>
                  <t.icon className="w-5 h-5" />
                </div>
                <div className="min-w-0">
                  <div className="font-extrabold text-slate-950 text-sm">{t.title}</div>
                  <div className="text-xs text-slate-600 font-semibold mt-1 leading-snug">{t.desc}</div>
                </div>
                <ArrowRight className="w-4 h-4 ml-auto self-center text-slate-400 group-hover:text-amber-600 group-hover:translate-x-1.5 transition-all shrink-0" />
              </Link>
            </motion.div>
          ))}
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lss-card p-6 lg:col-span-2 border-t-4 border-slate-950" data-testid="tracker-widget">
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-6">
            <div>
              <div className="lss-overline">Live Incentive Tracker</div>
              <div className="font-serif-lux text-2xl font-bold text-slate-950 mt-1">Staff earnings today</div>
            </div>
            <DatePicker
              testId="day-picker"
              value={day || todayIST()}
              onChange={(e) => setDay(e.target.value)}
            />
          </div>

          <div className="overflow-x-auto border border-slate-200 rounded-xl">
            <table className="lss-table">
              <thead>
                <tr>
                  <th>Staff</th>
                  <th className="text-right">Service ₹</th>
                  <th className="text-right">Retail ₹</th>
                  <th className="text-right">Bonus</th>
                  <th className="text-right">Commission</th>
                  <th className="text-right">Product Inc</th>
                  <th className="text-right">Card Bonus</th>
                  <th className="text-right">Total</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((r) => (
                  <tr key={r.staff_id} data-testid={`incentive-row-${r.staff_id}`}>
                    <td className="text-slate-950 font-extrabold">{r.staff_name}</td>
                    <td className="text-right tabular font-semibold text-slate-700">{money(r.service_revenue)}</td>
                    <td className="text-right tabular font-semibold text-slate-700">{money(r.retail_revenue)}</td>
                    <td className="text-right tabular font-extrabold text-amber-800">
                      {money(r.daily_bonus)}
                    </td>
                    <td className="text-right tabular font-semibold text-emerald-800">{money(r.retail_commission)}</td>
                    <td className="text-right tabular font-extrabold text-emerald-800">{money(r.product_incentive || 0)}</td>
                    <td className="text-right tabular font-extrabold text-amber-800">{money(r.prepaid_card_bonus || 0)}</td>
                    <td className="text-right tabular font-extrabold text-slate-950 bg-slate-50/50">
                      {money(r.total_earned)}
                    </td>
                  </tr>
                ))}
                {rows.length === 0 && (
                  <tr>
                    <td colSpan={8} className="text-center text-slate-500 font-medium py-8">
                      No transactions recorded for {day || "selected day"}
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>

        <div className="lss-card p-6 border-t-4 border-amber-500" data-testid="reorder-widget">
          <div className="flex items-center justify-between gap-3">
            <div className="lss-overline text-amber-800 flex items-center gap-2">
              Reorder Alerts
              {poNeeds.length > 0 && (
                <span className="relative flex h-2.5 w-2.5">
                  <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-amber-400 opacity-75"></span>
                  <span className="relative inline-flex rounded-full h-2.5 w-2.5 bg-amber-500"></span>
                </span>
              )}
            </div>
            <Link
              to="/procurement"
              className="text-[11px] font-extrabold uppercase tracking-wider text-amber-800 hover:text-amber-950 hover:underline shrink-0"
            >
              Raise POs →
            </Link>
          </div>

          <div className="font-serif-lux text-2xl font-bold text-slate-950 mt-1 mb-4">
            {poNeeds.length} SKUs need action
          </div>

          <div className="space-y-3 max-h-80 overflow-y-auto pr-1">
            {poNeeds.slice(0, 8).map((p) => (
              <motion.div
                key={p.sku_id}
                initial={{ opacity: 0, x: 5 }}
                animate={{ opacity: 1, x: 0 }}
                className="border-l-4 border-amber-500 bg-amber-50/40 p-3.5 rounded-r-xl shadow-xs border border-amber-200/60"
              >
                <div className="text-sm font-extrabold text-slate-950 truncate">
                  {p.sku_name}
                </div>
                <div className="text-xs text-slate-700 font-semibold mt-1">
                  Stock: <span className="font-extrabold text-slate-950">{p.on_hand}</span>
                </div>
              </motion.div>
            ))}
            {poNeeds.length === 0 && (
              <div className="text-sm text-slate-500 font-medium py-4">All stock levels are healthy.</div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
